<template>
  <v-dialog v-model="isOpen" width="800" persistent>
    <v-form ref="form" v-model="valid" @submit.prevent="save">
      <v-card>
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <v-alert v-if="hasNonFieldErrors" type="error">
            <ul>
              <li v-for="error in nonFieldErrors" :key="error">{{ error }}</li>
            </ul>
          </v-alert>
          <v-autocomplete
            v-if="hasMeetingChoices"
            v-model="meeting"
            :disabled="isInEditMode || areMetadataFieldsDisabled"
            :items="meetingChoices"
            :label="$t('Business_case_meeting')"
            :rules="[$rules.required]"
            item-text="title"
            item-value="id"
            return-object
            required
            clearable
            :error-messages="errorsForField('meeting')"
          />
          <v-autocomplete
            v-model="advance.business_type"
            :disabled="areMetadataFieldsDisabled"
            :items="businessTypeChoices"
            :label="$t('Business_type')"
            :rules="[$rules.required]"
            return-object
            required
            clearable
            :error-messages="errorsForField('business_type')"
          />
          <v-text-field
            v-model="advance.title"
            :disabled="areMetadataFieldsDisabled"
            :label="$t('Business_case_title')"
            :rules="[$rules.required]"
            required
            :error-messages="errorsForField('title')"
          />
          <v-textarea
            v-model="advance.description"
            :disabled="areMetadataFieldsDisabled"
            :label="$t('Wording')"
            :rules="[$rules.required]"
            required
            :error-messages="errorsForField('description')"
          />
          <v-textarea
            v-model="advance.reason"
            :disabled="areMetadataFieldsDisabled"
            :label="$t('Business_case_reason')"
          />
          <v-autocomplete
            v-model="firstSignatoryId"
            :items="committeeMembers"
            :label="$t('Business_case_advance_creator')"
            :rules="[$rules.required]"
            required
            :disabled="areMetadataFieldsDisabled || !user.can_create_advances_for_other_people"
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="secondSignatoryId"
            :disabled="areMetadataFieldsDisabled"
            :items="committeeMembers"
            :label="$t('Business_case_second_signature')"
            clearable
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="thirdSignatoryId"
            :disabled="areMetadataFieldsDisabled"
            :items="committeeMembers"
            :label="$t('Business_case_third_signature')"
            clearable
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="internalOtherSignatories"
            :disabled="areMetadataFieldsDisabled"
            :items="otherSignatoryChoices"
            :label="$t('Business_case_other_signature')"
            clearable
            :item-value="otherSignatoryValue"
            :item-text="otherSignatoryLabel"
            return-object
            multiple
          />
          <v-select
            v-model="advance.rights_holder"
            :disabled="areMetadataFieldsDisabled"
            :items="rightsHolderOptions"
            :label="$t('Business_case_rights_holder')"
            :error-messages="errorsForField('rights_holder')"
            clearable
          />
          <div class="d-flex">
            <v-checkbox
              v-model="advance.is_urgent"
              :disabled="areMetadataFieldsDisabled"
              class="mr-3"
              :label="$t('mark_urgent')"
            />
          </div>

          <v-file-input
            v-model="attachments"
            :disabled="isSaving"
            small-chips
            multiple
            :label="$t('Select_file')"
            :error-messages="attachmentErrors"
            :error-count="attachmentErrors.length"
            @change="handleFileChange"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="error" :disabled="isSaving" @click="cancel">{{ $t('Cancel') }}</v-btn>
          <v-btn text type="submit" data-testid="save-advance" color="primary" :loading="isSaving" :disabled="isSaving">
            {{ saveActionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import clone from 'lodash/cloneDeep'
import escape from 'lodash/escape'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import Committee from '@/store/models/committee'
import Meeting from '@/store/models/meeting'
import Profile from '@/store/models/profile'
import User from '@/store/models/user'

function advanceFactory() {
  return {
    first_signatory: { id: null, signatory_id: Profile.query().first().id, role: 'FIRST_SIGNATORY', type: 'user' },
    other_signatories: [],
  }
}

function memberLabel(member) {
  return [member.first_name, member.last_name].filter(Boolean).join(' ') + ` (${member.username})`
}

export default {
  name: 'AdvanceDialog',
  data() {
    return {
      isOpen: false,
      advance: {},
      valid: null,
      rightsHolderOptions: [
        { text: this.$t('Business_case_advance_creator'), value: 'advance_creator' },
        { text: this.$t('Business_case_faction'), value: 'faction' },
        { text: this.$t('Business_case_commission'), value: 'commission' },
        { text: this.$t('Business_case_cross_party'), value: 'cross_party' },
      ],
      errors: {},
      isSaving: false,
      hasSavedMetadata: false,
      businessTypeChoices: [],
      meetingChoices: [],
      meeting: {},
      attachments: [],
      isAttachmentChanged: false,
      otherSignatoryTypes: {
        user: this.$t('member'),
        committee: this.$t('Committee'),
        committeegroup: this.$t('Committee_Group'),
      },
      committees: [],
      committeeGroups: [],
      loadingAsyncData: true,
      firstSignatoryId: Profile.query().first(),
      secondSignatoryId: null,
      thirdSignatoryId: null,
      internalOtherSignatories: [],
      openSequence: 0,
    }
  },
  computed: {
    areMetadataFieldsDisabled() {
      return this.isSaving || this.hasSavedMetadata
    },
    committeeMembers() {
      return User.all().filter((user) => user.roles.includes('member'))
    },
    otherSignatoryChoices() {
      const members = this.committeeMembers.map((member) => ({
        id: member.id,
        label: memberLabel(member),
        type: 'user',
      }))
      const committees = this.committees.map((committee) => ({
        id: committee.id,
        label: committee.title,
        type: 'committee',
      }))
      const committeeGroups = this.committeeGroups.map((committeeGroup) => ({
        id: committeeGroup.id,
        label: committeeGroup.title,
        type: 'committeegroup',
      }))
      return [...committees, ...committeeGroups, ...members]
    },
    user() {
      return Profile.query().first()
    },
    nonFieldErrors() {
      return this.errorsForField('non_field_errors')
    },
    attachmentErrors() {
      const errors = this.errorsForField('attachments')
      if (!errors) return []
      if (typeof errors === 'string') return [errors]
      return Object.values(errors).reduce((messages, messagesForFile) => messages.concat(messagesForFile), [])
    },
    hasNonFieldErrors() {
      return !isEmpty(this.nonFieldErrors)
    },
    isInEditMode() {
      return !isUndefined(this.advance.id)
    },
    dialogTitle() {
      return this.isInEditMode ? this.$t('updateAdvanceTitle') : this.$t('createAdvanceTitle')
    },
    saveActionLabel() {
      return this.isInEditMode ? this.$t('modifyAdvanceButton') : this.$t('newAdvanceButton')
    },
    saveSuccessMessage() {
      return this.isInEditMode ? this.$t('advanceCardModifySuccess') : this.$t('advanceCardCreateSuccess')
    },
    hasMeetingChoices() {
      return !isEmpty(this.meetingChoices)
    },
  },
  watch: {
    firstSignatoryId(signatory) {
      this.advance.first_signatory = this.transform_user_signatory(signatory, 'FIRST_SIGNATORY')
    },
    secondSignatoryId(signatory) {
      this.advance.second_signatory = this.transform_user_signatory(signatory, 'SECOND_SIGNATORY')
    },
    thirdSignatoryId(signatory) {
      this.advance.third_signatory = this.transform_user_signatory(signatory, 'THIRD_SIGNATORY')
    },
    internalOtherSignatories(newList) {
      const selectedKeys = new Set(newList.map((signatory) => `${signatory.type}:${signatory.id}`))

      this.advance.other_signatories = (this.advance.other_signatories || []).filter((signatory) => {
        if (signatory.role !== 'OTHER_SIGNATORY') return true
        const key = `${signatory.type}:${signatory.signatory_id}`
        return selectedKeys.has(key)
      })

      for (const signatory of newList) {
        const key = `${signatory.type}:${signatory.id}`
        const alreadyExists = this.advance.other_signatories.some(
          (existing) => existing.role === 'OTHER_SIGNATORY' && `${existing.type}:${existing.signatory_id}` === key,
        )
        if (!alreadyExists) {
          this.advance.other_signatories.push({
            id: null,
            role: 'OTHER_SIGNATORY',
            signatory_id: signatory.id,
            type: signatory.type,
          })
        }
      }
    },
  },
  async mounted() {
    try {
      const businessTypes = await api.get(createLink('businesstypes'), { params: { show_for_advance: true } })
      this.businessTypeChoices = businessTypes.data.map((businesstype) => ({
        text: businesstype.title,
        value: businesstype.api_uid,
      }))
    } finally {
      this.loadingAsyncData = false
    }
  },

  methods: {
    beginOpen() {
      this.openSequence += 1
      this.isOpen = false
      this.errors = {}
      this.hasSavedMetadata = false
      this.advance = { other_signatories: [] }
      this.meeting = {}
      this.meetingChoices = []
      this.attachments = []
      this.isAttachmentChanged = false
      this.internalOtherSignatories = []
      this.firstSignatoryId = null
      this.secondSignatoryId = null
      this.thirdSignatoryId = null
      this.committees = []
      this.committeeGroups = []
      return this.openSequence
    },
    resetSignatoryIds() {
      this.firstSignatoryId = Profile.query().first()
      this.secondSignatoryId = null
      this.thirdSignatoryId = null
    },
    handleFileChange(files) {
      this.attachments = files
      this.isAttachmentChanged = true
      this.$delete(this.errors, 'attachments')
    },
    signatoryLabel(signatory) {
      return memberLabel(signatory)
    },
    otherSignatoryValue(signatory) {
      const signatoryType = get(this.otherSignatoryTypes, signatory.type, 'unknown')
      return `${signatory.id} - ${signatoryType}`
    },
    otherSignatoryLabel(signatory) {
      const signatoryType = get(this.otherSignatoryTypes, signatory.type, 'unknown')
      return `${signatory.label} - ${signatoryType}`
    },
    getIdForAdvanceSignatory(role) {
      switch (role) {
        case 'FIRST_SIGNATORY':
          return this.advance.first_signatory?.advance_signatory_id || null
        case 'SECOND_SIGNATORY':
          return this.advance.second_signatory?.advance_signatory_id || null
        case 'THIRD_SIGNATORY':
          return this.advance.third_signatory?.advance_signatory_id || null
        default:
          return null
      }
    },
    transform_user_signatory(user, role) {
      if (!user) return null
      return {
        id: this.getIdForAdvanceSignatory(role),
        signatory_id: user.id,
        role,
        type: 'user',
      }
    },
    cancel() {
      this.openSequence += 1
      this.$refs.form.resetValidation()
      this.isOpen = false
    },
    errorsForField(field) {
      return get(this.errors, field, [])
    },
    async attachAttachments(advance) {
      const formData = new FormData()
      this.attachments.forEach((attachment) => formData.append('attachments', attachment))
      await api.patch(createLink('advance/{id}/attach_attachments', { id: advance.id }), formData)
    },
    async save() {
      if (this.isSaving) return
      this.isSaving = true
      this.errors = {}
      const isEditing = this.isInEditMode
      const sequence = this.openSequence
      const successMessage = this.saveSuccessMessage
      let metadataSaved = false
      try {
        if (!this.hasSavedMetadata) {
          const metadata = clone(this.advance)
          const { data: advance } = isEditing
            ? await api.patch(createLink('advance/{id}', { id: metadata.id }), metadata)
            : await api.post(createLink('advance'), { meeting: this.meeting.id, ...metadata })
          metadataSaved = true
          if (sequence !== this.openSequence) return
          // Keep the write-shaped signatories; the response contains read-shaped signatories.
          this.$set(this.advance, 'id', advance.id)
          this.hasSavedMetadata = true
        }
        metadataSaved = true

        if (this.isAttachmentChanged && !isEmpty(this.attachments)) {
          await this.attachAttachments(this.advance)
          if (sequence !== this.openSequence) return
        }
        this.isAttachmentChanged = false

        this.notifySuccess(successMessage)
        this.cancel()
      } catch (error) {
        if (sequence !== this.openSequence) return
        const responseData = get(error, 'response.data')
        this.errors = responseData && typeof responseData === 'object' ? responseData : {}
        const nonFieldErrors = get(error, 'response.data.non_field_errors', []).join(' ')
        const messageDetail = get(error, 'response.data.detail', null)
        const messageText = get(error, 'response.data.text', null)
        const messageToDisplay =
          escape(this.attachmentErrors.join(' ')) ||
          messageText ||
          messageDetail ||
          nonFieldErrors ||
          error.message ||
          this.$t('unknown_error')
        let errorMessage
        if (metadataSaved) {
          const status = get(error, 'response.status')
          const canRetry = !error.response || (status >= 500 && status < 600)
          errorMessage = canRetry
            ? this.$t('advanceCardUploadError', { message: messageToDisplay })
            : this.$t('advanceCardUploadRejected', { message: messageToDisplay })
        } else {
          errorMessage = isEditing
            ? this.$t('advanceCardModifyError', { message: messageToDisplay })
            : this.$t('advanceCardCreateError', { message: messageToDisplay })
        }

        this.notifyError(errorMessage)
        throw error
      } finally {
        if (metadataSaved) this.$emit('update')
        this.isSaving = false
      }
    },
    async openWithCommittee(committee) {
      const sequence = this.beginOpen()
      await User.fetchAllByModel(committee)
      await Meeting.fetchAllByModel(committee)
      if (sequence !== this.openSequence) return
      this.meetingChoices = Meeting.query().where('committee_id', committee.id).all()
      this.advance = advanceFactory()
      this.resetSignatoryIds()
      this.isOpen = true
    },
    async openWithMeeting(meeting) {
      const sequence = this.beginOpen()
      const committee = await Committee.findOrFetch(meeting.committee_id)
      await User.fetchAllByModel(committee)
      const { data: committees } = await api.get(createLink('committees/listing'))
      const { data: committeeGroups } = await api.get(
        createLink('committees/{id}/committee_groups', { id: committee.id }),
      )
      if (sequence !== this.openSequence) return
      this.meeting = meeting
      this.advance = advanceFactory()
      this.committees = committees
      this.committeeGroups = committeeGroups
      this.resetSignatoryIds()
      this.isOpen = true
    },
    async openWithAdvance(advance) {
      const sequence = this.beginOpen()
      const sourceAdvance = clone(advance)
      const meeting = await Meeting.findOrFetch(sourceAdvance.meeting_id)
      await Committee.fetchAll()
      const committee = await Committee.query().find(meeting.committee_id)
      await User.fetchAllByModel(committee)
      const normalizedOtherSignatories = (sourceAdvance.other_signatories || []).map((signatory) => ({
        id: signatory.advance_signatory_id,
        role: 'OTHER_SIGNATORY',
        signatory_id: signatory.id,
        type: signatory.type,
      }))

      // Fetch committee and groups
      const { data: committees } = await api.get(createLink('committees/listing'))
      const { data: committeeGroups } = await api.get(
        createLink('committees/{id}/committee_groups', { id: committee.id }),
      )
      if (sequence !== this.openSequence) return
      this.meeting = meeting
      this.attachments = sourceAdvance.attachments || []
      this.isAttachmentChanged = false
      this.committees = committees
      this.committeeGroups = committeeGroups
      this.advance = {
        ...sourceAdvance,
        other_signatories: normalizedOtherSignatories,
      }
      this.firstSignatoryId = sourceAdvance.first_signatory
      this.secondSignatoryId = sourceAdvance.second_signatory
      this.thirdSignatoryId = sourceAdvance.third_signatory
      this.internalOtherSignatories = (sourceAdvance.other_signatories || []).map((signatory) => {
        return {
          id: signatory.id,
          type: signatory.type,
        }
      })

      this.isOpen = true
    },
  },
}
</script>
